import React from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Plus } from 'lucide-react';
import { useUser } from '@/hooks/auth/use-user';
import { useAIAgentChat } from '@/contexts/ai-agents';
import { useChatManager } from '@/hooks/ai-agents/useChatManager';
import { useLocalStorageState } from '@/contexts/ai-agents/LocalStorageStateContext';
import { AIAgentChatInterfaceDesktop } from './AIAgentChatInterfaceDesktop';
import { CopilotIcon } from '@/components/icons';
import { useAIUsageData } from '@/hooks/subscription/useAIUsageData';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

interface DesktopChatPanelProps {
  onClose?: () => void;
}

/**
 * DesktopChatPanel - Right panel for desktop chat layout
 * 
 * Shows either:
 * - Chat interface when a thread is selected
 * - Empty state when no thread is selected
 * 
 * Uses existing chat system architecture:
 * - useChatManager for state management
 * - AIAgentChatInterfaceDesktop for chat UI
 * - localStorage-first reactive system
 */
export const DesktopChatPanel: React.FC<DesktopChatPanelProps> = ({ onClose }) => {
  const { user } = useUser();
  const { currentAssistant } = useAIAgentChat();
  const { activeThreadId, messages, chatState } = useChatManager(user, currentAssistant);
  const { inputValue, setInputValue, setCurrentThreadId } = useLocalStorageState();
  const usageData = useAIUsageData();
  const { openOutOfCreditsModal } = useGlobalModals();
  const chatManager = useChatManager(user, currentAssistant);

  // Handle creating a new conversation
  const handleStartConversation = async () => {
    try {
      // ✅ UPFRONT CREDIT CHECK - Prevent operation if no credits available
      if (usageData.remainingBalance === 0) {
        console.log('[DesktopChatPanel] Insufficient credits - blocking conversation creation and showing modal');
        openOutOfCreditsModal();
        return; // Clean early exit - no cascading errors
      }

      console.log(`[DesktopChatPanel] Credits available (${usageData.remainingBalance}) - proceeding with conversation creation`);
      
      // ✅ NEW THREAD: Complete creation workflow
      console.log('[DesktopChatPanel] Creating new thread via ChatManager');
      
      // Use ChatManager to create new thread
      const newThreadId = await chatManager.loadThread();
      
      if (newThreadId) {
        // Set the new thread ID (triggers UI updates)
        setCurrentThreadId(newThreadId);
        
        // ✅ CACHE INVALIDATION: Emit event for threads list refresh
        window.dispatchEvent(new CustomEvent('localStorage-change', {
          detail: { key: 'newThreadCreated', threadId: newThreadId }
        }));
        
        console.log('[DesktopChatPanel] New thread created successfully:', newThreadId);
        
        // Focus the input after a short delay to ensure UI has updated
        setTimeout(() => {
          const messageInput = document.querySelector('[data-copilot-input="true"]') as HTMLTextAreaElement;
          if (messageInput) {
            messageInput.focus();
          }
        }, 300);
      } else {
        console.error('[DesktopChatPanel] Failed to create new thread');
      }
    } catch (error) {
      console.error('[DesktopChatPanel] Thread creation failed:', error);
    }
  };

  // Show empty state when no thread is selected
  if (!activeThreadId) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 bg-background/50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center space-y-6 max-w-md"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center"
          >
            <MessageSquare className="w-10 h-10 text-muted-foreground/60" />
          </motion.div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="space-y-2"
          >
            <h3 className="text-xl font-medium text-foreground">
              Start a new conversation
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Begin chatting with {currentAssistant?.name || 'your AI assistant'} to get help with your tasks, generate content, or explore ideas.
            </p>
          </motion.div>

          {/* Start Conversation Button */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="flex justify-center"
          >
            <motion.button
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStartConversation}
              className="group relative overflow-hidden bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
            >
              {/* Background animation */}
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
              
              {/* Button content */}
              <div className="relative flex items-center gap-2">
                <Plus className="w-4 h-4" />
                <span>Start Conversation</span>
              </div>
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // Show chat interface when thread is selected
  return (
    <div className="h-full bg-background">
      <AIAgentChatInterfaceDesktop 
        threadId={activeThreadId}
        inputValue={inputValue}
        onInputChange={setInputValue}
        onClose={onClose}
      />
    </div>
  );
}; 